/**
 * Copilot Studio maker-evaluation tools backed by the supported Power Platform
 * 2024-10-01 REST contract.
 */

import { createHash, randomUUID } from "node:crypto";
import { constants as fsConstants } from "node:fs";
import { link, lstat, open, realpath, rename, unlink } from "node:fs/promises";
import { basename, dirname, extname, isAbsolute, join, resolve } from "node:path";
import type { Tool } from "@modelcontextprotocol/sdk/types.js";
import { z } from "zod";
import {
  COPILOT_EVALUATION_DEFAULT_LIST_LIMIT,
  COPILOT_EVALUATION_DEFAULT_RESULT_LIMIT,
  COPILOT_EVALUATION_MAX_LIST_LIMIT,
  COPILOT_EVALUATION_MAX_RESULT_LIMIT,
  COPILOT_EVALUATION_MAX_SNAPSHOT_BYTES,
  type CopilotEvaluationApi,
  type CopilotEvaluationCollection,
  type CopilotEvaluationRun,
  type CopilotEvaluationTestSet,
} from "../api/copilot-evaluation-api.js";
import { ApiError, ConfirmationRequiredError } from "../utils/errors.js";
import { GUID_PATTERN } from "../utils/security.js";

const MAX_LOCAL_PATH_LENGTH = 4_096;
const MAX_RUN_NAME_LENGTH = 200;
const MAX_CONNECTION_ID_LENGTH = 512;
const MAX_TOOL_CONNECTION_GROUPS = 100;
const MAX_CONNECTIONS_PER_GROUP = 100;

function hasControlCharacter(value: string): boolean {
  return Array.from(value).some((character) => {
    const codePoint = character.codePointAt(0) ?? 0;
    return codePoint <= 0x1f || codePoint === 0x7f;
  });
}

const guidSchema = (field: string) => z.string().regex(GUID_PATTERN, `${field} must be a GUID`);
const environmentSchema = z
  .string()
  .min(1)
  .max(128)
  .optional()
  .describe("Environment GUID/name. Defaults to the session's current environment.");
const connectionIdentifierSchema = z
  .string()
  .min(1)
  .max(MAX_CONNECTION_ID_LENGTH)
  .regex(
    /^[A-Za-z0-9_./-]+$/,
    "connection identifiers may contain letters, digits, period, underscore, slash, and hyphen",
  );

const makerEvaluationConnectionSchema = z
  .object({
    connectorId: connectionIdentifierSchema,
    connectionId: connectionIdentifierSchema,
    connectionReferenceName: connectionIdentifierSchema,
  })
  .strict();

const toolConnectionsSchema = z
  .object({
    botId: guidSchema("toolsConnections botId"),
    botSchemaName: z
      .string()
      .min(1)
      .max(200)
      .regex(/^[A-Za-z_][A-Za-z0-9_]*$/, "botSchemaName must be a Dataverse schema name"),
    connections: z.array(makerEvaluationConnectionSchema).max(MAX_CONNECTIONS_PER_GROUP),
  })
  .strict();

const agentInputShape = {
  agentId: guidSchema("agentId"),
  environment: environmentSchema,
};

export const listCopilotEvaluationTestSetsInputSchema = z
  .object({
    ...agentInputShape,
    limit: z.number().int().min(1).max(COPILOT_EVALUATION_MAX_LIST_LIMIT).optional(),
  })
  .strict();

export const getCopilotEvaluationTestSetInputSchema = z
  .object({
    ...agentInputShape,
    testSetId: guidSchema("testSetId"),
  })
  .strict();

export const startCopilotEvaluationInputSchema = z
  .object({
    ...agentInputShape,
    testSetId: guidSchema("testSetId"),
    evaluationRunName: z
      .string()
      .trim()
      .min(1)
      .max(MAX_RUN_NAME_LENGTH)
      .refine(
        (value) => !hasControlCharacter(value),
        "evaluationRunName cannot contain control characters",
      )
      .optional(),
    mcsConnectionId: connectionIdentifierSchema.optional(),
    runOnPublishedBot: z.boolean().optional(),
    toolsConnections: z.array(toolConnectionsSchema).max(MAX_TOOL_CONNECTION_GROUPS).optional(),
    confirmRun: z.boolean().optional(),
    includeErrorDiagnostics: z.boolean().optional(),
  })
  .strict();

export const listCopilotEvaluationRunsInputSchema = z
  .object({
    ...agentInputShape,
    limit: z.number().int().min(1).max(COPILOT_EVALUATION_MAX_LIST_LIMIT).optional(),
  })
  .strict();

export const getCopilotEvaluationRunInputSchema = z
  .object({
    ...agentInputShape,
    evaluationRunId: guidSchema("evaluationRunId"),
    maxTestCaseResults: z.number().int().min(1).max(COPILOT_EVALUATION_MAX_RESULT_LIMIT).optional(),
  })
  .strict();

export const downloadCopilotEvaluationSnapshotInputSchema = z
  .object({
    ...agentInputShape,
    evaluationRunId: guidSchema("evaluationRunId"),
    outputFilePath: z.string().min(1).max(MAX_LOCAL_PATH_LENGTH),
    confirmDownload: z.boolean().optional(),
    overwriteExisting: z.boolean().optional(),
  })
  .strict();

const guidProperty = (description: string) => ({
  type: "string" as const,
  pattern: GUID_PATTERN.source,
  description,
});

const environmentProperty = {
  type: "string" as const,
  minLength: 1,
  maxLength: 128,
  description: "Environment GUID/name. Defaults to the session's current environment.",
};

const limitProperty = {
  type: "integer" as const,
  minimum: 1,
  maximum: COPILOT_EVALUATION_MAX_LIST_LIMIT,
  default: COPILOT_EVALUATION_DEFAULT_LIST_LIMIT,
  description:
    "Maximum items to return. The formal API does not expose paging; the result says when additional service items were omitted.",
};

const agentProperties = {
  agentId: guidProperty("Copilot Studio agent (Dataverse bot) GUID."),
  environment: environmentProperty,
};

const connectionIdentifierProperty = {
  type: "string" as const,
  minLength: 1,
  maxLength: MAX_CONNECTION_ID_LENGTH,
  pattern: "^[A-Za-z0-9_./-]+$",
};

const toolConnectionsJsonSchema = {
  type: "object" as const,
  additionalProperties: false,
  properties: {
    botId: guidProperty("Dataverse bot GUID whose tool connections are supplied."),
    botSchemaName: {
      type: "string" as const,
      minLength: 1,
      maxLength: 200,
      pattern: "^[A-Za-z_][A-Za-z0-9_]*$",
    },
    connections: {
      type: "array" as const,
      maxItems: MAX_CONNECTIONS_PER_GROUP,
      items: {
        type: "object" as const,
        additionalProperties: false,
        properties: {
          connectorId: connectionIdentifierProperty,
          connectionId: connectionIdentifierProperty,
          connectionReferenceName: connectionIdentifierProperty,
        },
        required: ["connectorId", "connectionId", "connectionReferenceName"],
      },
    },
  },
  required: ["botId", "botSchemaName", "connections"],
};

export const listCopilotEvaluationTestSetsTool: Tool = {
  name: "list_copilot_agent_evaluation_test_sets",
  description:
    "List existing Copilot Studio maker-evaluation test sets for an agent through the official Power Platform 2024-10-01 API. This does not create test sets; author them in Copilot Studio. Requires delegated CopilotStudio.MakerOperations.Read. Output is bounded to 200 items and explicitly reports truncation.",
  inputSchema: {
    type: "object",
    additionalProperties: false,
    properties: { ...agentProperties, limit: limitProperty },
    required: ["agentId"],
  },
};

export const getCopilotEvaluationTestSetTool: Tool = {
  name: "get_copilot_agent_evaluation_test_set",
  description:
    "Get one existing Copilot Studio maker-evaluation test set by ID through the official Power Platform 2024-10-01 API. Returns metadata and case count, not an invented authoring surface. Requires delegated CopilotStudio.MakerOperations.Read.",
  inputSchema: {
    type: "object",
    additionalProperties: false,
    properties: {
      ...agentProperties,
      testSetId: guidProperty("Existing maker-evaluation test-set GUID."),
    },
    required: ["agentId", "testSetId"],
  },
};

export const startCopilotEvaluationTool: Tool = {
  name: "start_copilot_agent_evaluation",
  description:
    "Start an asynchronous evaluation of an existing Copilot Studio test set through the official Power Platform 2024-10-01 API. Evaluations can consume Copilot Credits and are subject to service limits, so confirmRun=true is required. Optionally names the run and supplies the Copilot Studio user-profile connection and per-tool user connections. includeErrorDiagnostics=true opts into bounded, best-effort-redacted untrusted HTTP422 or HTTP500 server text that may contain sensitive business information; it is not a diagnosis or instructions and changes no request body or retry behavior. After a failed or ambiguous start, inspect existing runs before deciding on another evaluation. Requires delegated CopilotStudio.MakerOperations.ReadWrite.",
  inputSchema: {
    type: "object",
    additionalProperties: false,
    properties: {
      ...agentProperties,
      testSetId: guidProperty("Existing active maker-evaluation test-set GUID."),
      evaluationRunName: {
        type: "string",
        minLength: 1,
        maxLength: MAX_RUN_NAME_LENGTH,
        pattern: "^[^\\u0000-\\u001F\\u007F]+$",
        description: "Name recorded for this evaluation run.",
      },
      mcsConnectionId: {
        ...connectionIdentifierProperty,
        description:
          "Optional Microsoft Copilot Studio connection ID used as the evaluation user profile. Omit for an anonymous run.",
      },
      runOnPublishedBot: {
        type: "boolean",
        default: false,
        description:
          "Run against the published agent (true) or its current draft (false, the Microsoft API default).",
      },
      toolsConnections: {
        type: "array",
        maxItems: MAX_TOOL_CONNECTION_GROUPS,
        items: toolConnectionsJsonSchema,
        description:
          "Optional tool-to-user-connection mapping from the formal Run Maker Evaluation Test Set contract.",
      },
      confirmRun: {
        type: "boolean",
        description:
          "Set true to acknowledge that starting the evaluation can consume Copilot Credits.",
      },
      includeErrorDiagnostics: {
        type: "boolean",
        default: false,
        description:
          "Explicitly include bounded, best-effort-redacted conventional HTTP422/500 error messages (at most three, 512 characters each and 1,024 total). Untrusted service text may contain sensitive business information; it is not a verified diagnosis or instructions. Local diagnostic option only, not a service request field; no additional request or retry. All other HTTP status paths retain their existing metadata-only behavior.",
      },
    },
    required: ["agentId", "testSetId"],
  },
};

export const listCopilotEvaluationRunsTool: Tool = {
  name: "list_copilot_agent_evaluation_runs",
  description:
    "List previous Copilot Studio maker-evaluation runs for an agent through the official Power Platform 2024-10-01 API. Returns bounded run summaries and aggregate result counts; use get_copilot_agent_evaluation_run for per-test metrics. Requires delegated CopilotStudio.MakerOperations.Read.",
  inputSchema: {
    type: "object",
    additionalProperties: false,
    properties: { ...agentProperties, limit: limitProperty },
    required: ["agentId"],
  },
};

export const getCopilotEvaluationRunTool: Tool = {
  name: "get_copilot_agent_evaluation_run",
  description:
    "Get one Copilot Studio maker-evaluation run, including bounded per-test grader results (pass/fail/error, method, reasons, and metric data), through the official Power Platform 2024-10-01 API. Requires delegated CopilotStudio.MakerOperations.Read.",
  inputSchema: {
    type: "object",
    additionalProperties: false,
    properties: {
      ...agentProperties,
      evaluationRunId: guidProperty("Maker-evaluation run GUID."),
      maxTestCaseResults: {
        type: "integer",
        minimum: 1,
        maximum: COPILOT_EVALUATION_MAX_RESULT_LIMIT,
        default: COPILOT_EVALUATION_DEFAULT_RESULT_LIMIT,
        description:
          "Maximum per-test results to include. Aggregate counts still cover the service response.",
      },
    },
    required: ["agentId", "evaluationRunId"],
  },
};

export const downloadCopilotEvaluationSnapshotTool: Tool = {
  name: "download_copilot_agent_evaluation_snapshot",
  description:
    "Download the agent-content snapshot attached to a Copilot Studio maker-evaluation run as a bounded local ZIP. Requires an explicit absolute .zip outputFilePath and confirmDownload=true; existing files are preserved unless overwriteExisting=true. The response must be application/zip, have a ZIP signature, and be no larger than 64 MiB. Requires delegated CopilotStudio.MakerOperations.Read.",
  inputSchema: {
    type: "object",
    additionalProperties: false,
    properties: {
      ...agentProperties,
      evaluationRunId: guidProperty("Maker-evaluation run GUID."),
      outputFilePath: {
        type: "string",
        minLength: 1,
        maxLength: MAX_LOCAL_PATH_LENGTH,
        description: "Absolute, normalized local .zip destination path.",
      },
      confirmDownload: {
        type: "boolean",
        description: "Set true to permit the network download and local file creation.",
      },
      overwriteExisting: {
        type: "boolean",
        description:
          "Set true to atomically replace an existing regular file. Has no effect for a new path.",
      },
    },
    required: ["agentId", "evaluationRunId", "outputFilePath"],
  },
};

export const copilotEvaluationTools: Tool[] = [
  listCopilotEvaluationTestSetsTool,
  getCopilotEvaluationTestSetTool,
  startCopilotEvaluationTool,
  listCopilotEvaluationRunsTool,
  getCopilotEvaluationRunTool,
  downloadCopilotEvaluationSnapshotTool,
];

export interface CopilotEvaluationRichResult {
  text: string;
  structured: Record<string, unknown>;
}

function plural(count: number, singular: string): string {
  return `${count} ${singular}${count === 1 ? "" : "s"}`;
}

function testCaseCount(count: number | undefined): string {
  return count === undefined ? "test-case count not reported" : plural(count, "test case");
}

function formatTestSet(testSet: CopilotEvaluationTestSet): string {
  return (
    `• ${testSet.displayName ?? "Unnamed test set"} — ${testSet.id}\n` +
    `  ${testSet.state}; ${testCaseCount(testSet.totalTestCases)}`
  );
}

function formatCountMap(values: Record<string, number | undefined>): string {
  const parts = Object.entries(values)
    .filter((entry): entry is [string, number] => entry[1] !== undefined && entry[1] > 0)
    .map(([name, count]) => `${name} ${count}`);
  return parts.length > 0 ? parts.join(", ") : "none reported";
}

function formatRunSummary(run: CopilotEvaluationRun): string {
  const timing = run.startTime
    ? `${run.startTime}${run.endTime ? ` to ${run.endTime}` : ""}`
    : "time not reported";
  const progress =
    run.testCasesProcessed !== undefined
      ? `; progress ${run.testCasesProcessed} of ${run.totalTestCases ?? "unknown"}`
      : "";
  const reportedCount =
    run.totalTestCases ?? (run.testCasesReported > 0 ? run.testCasesReported : undefined);
  return [
    `• ${run.name ?? "Unnamed evaluation"} — ${run.id}`,
    `  ${run.state}${run.executionState ? ` / ${run.executionState}` : ""}; ${testCaseCount(reportedCount)}${progress}; ${timing}`,
    `  Test states: ${formatCountMap(run.testCaseStateCounts)}`,
    `  Metric outcomes: ${formatCountMap(run.metricStatusCounts)}`,
  ].join("\n");
}

function collectionResult<T>(
  label: string,
  collection: CopilotEvaluationCollection<T>,
  lines: string[],
): CopilotEvaluationRichResult {
  const header = `${label}: ${collection.returned} of ${collection.total}`;
  const truncation = collection.truncated
    ? "The formal API returned more items than this bounded MCP call can include. Use a known ID with the corresponding get tool."
    : undefined;
  return {
    text: [header, ...(lines.length > 0 ? ["", ...lines] : ["", "No items found."]), truncation]
      .filter((line): line is string => line !== undefined)
      .join("\n"),
    structured: collection as unknown as Record<string, unknown>,
  };
}

export async function handleListCopilotEvaluationTestSets(
  api: CopilotEvaluationApi,
  input: unknown,
): Promise<CopilotEvaluationRichResult> {
  const parsed = listCopilotEvaluationTestSetsInputSchema.parse(input);
  const result = await api.listTestSets(parsed.agentId, {
    environmentId: parsed.environment,
    limit: parsed.limit,
  });
  return collectionResult(
    "Copilot Studio evaluation test sets",
    result,
    result.items.map(formatTestSet),
  );
}

export async function handleGetCopilotEvaluationTestSet(
  api: CopilotEvaluationApi,
  input: unknown,
): Promise<CopilotEvaluationRichResult> {
  const parsed = getCopilotEvaluationTestSetInputSchema.parse(input);
  const testSet = await api.getTestSet(parsed.agentId, parsed.testSetId, parsed.environment);
  const lines = [
    formatTestSet(testSet),
    `Description: ${testSet.description ?? "not reported"}`,
    `Created: ${testSet.auditInfo?.createdTimeUtc ?? "not reported"}`,
    `Modified: ${testSet.auditInfo?.modifiedTimeUtc ?? "not reported"}`,
  ];
  return { text: lines.join("\n"), structured: { testSet } };
}

export async function handleStartCopilotEvaluation(
  api: CopilotEvaluationApi,
  input: unknown,
): Promise<CopilotEvaluationRichResult> {
  const parsed = startCopilotEvaluationInputSchema.parse(input);
  const displayName = parsed.evaluationRunName ?? `test set ${parsed.testSetId}`;
  if (!parsed.confirmRun) {
    throw new ConfirmationRequiredError(
      `Evaluation for ${displayName} was not started. It can consume Copilot Credits and is ` +
        "subject to service limits. " +
        "Set confirmRun=true to start it.",
    );
  }
  const result = await api.startEvaluation(parsed.agentId, parsed.testSetId, {
    evaluationRunName: parsed.evaluationRunName,
    mcsConnectionId: parsed.mcsConnectionId,
    runOnPublishedBot: parsed.runOnPublishedBot,
    toolsConnections: parsed.toolsConnections,
    environmentId: parsed.environment,
    ...(parsed.includeErrorDiagnostics === undefined
      ? {}
      : { includeErrorDiagnostics: parsed.includeErrorDiagnostics }),
  });
  return {
    text: [
      `Copilot Studio evaluation started: ${displayName}`,
      `Run ID: ${result.runId}`,
      `State: ${result.state}; execution: ${result.executionState}`,
      `Progress: ${result.testCasesProcessed ?? "unknown"} of ${result.totalTestCases ?? "unknown"} test cases`,
      "Use get_copilot_agent_evaluation_run with the run ID to read status and results.",
    ].join("\n"),
    structured: { run: result },
  };
}

export async function handleListCopilotEvaluationRuns(
  api: CopilotEvaluationApi,
  input: unknown,
): Promise<CopilotEvaluationRichResult> {
  const parsed = listCopilotEvaluationRunsInputSchema.parse(input);
  const result = await api.listRuns(parsed.agentId, {
    environmentId: parsed.environment,
    limit: parsed.limit,
  });
  return collectionResult(
    "Copilot Studio evaluation runs",
    result,
    result.items.map(formatRunSummary),
  );
}

export async function handleGetCopilotEvaluationRun(
  api: CopilotEvaluationApi,
  input: unknown,
): Promise<CopilotEvaluationRichResult> {
  const parsed = getCopilotEvaluationRunInputSchema.parse(input);
  const run = await api.getRun(parsed.agentId, parsed.evaluationRunId, {
    environmentId: parsed.environment,
    maxTestCaseResults: parsed.maxTestCaseResults,
  });
  const resultLines = run.testCasesResults.map((testCase) => {
    const metrics = testCase.metricsResults
      .map((metric) => `${metric.type}: ${metric.result.status}`)
      .join(", ");
    return `• ${testCase.testCaseId} — ${testCase.state}; ${metrics || "no metrics"}`;
  });
  const truncation = run.testCasesTruncated
    ? `Only ${run.testCasesReturned} of ${run.testCasesReported} per-test results are included. Raise maxTestCaseResults up to ${COPILOT_EVALUATION_MAX_RESULT_LIMIT} if needed.`
    : undefined;
  return {
    text: [formatRunSummary(run), "", ...resultLines, truncation]
      .filter((line): line is string => line !== undefined)
      .join("\n"),
    structured: { run },
  };
}

interface SnapshotDestination {
  path: string;
  exists: boolean;
  existing?: { dev: number; ino: number; size: number; mtimeMs: number };
}

async function resolveSnapshotDestination(filePath: string): Promise<SnapshotDestination> {
  if (
    !isAbsolute(filePath) ||
    resolve(filePath) !== filePath ||
    filePath.includes("\0") ||
    extname(filePath).toLowerCase() !== ".zip"
  ) {
    throw new ApiError(
      "Snapshot outputFilePath must be an absolute, normalized .zip path without traversal",
      400,
    );
  }
  const canonicalParent = await realpath(dirname(filePath));
  const parentMetadata = await lstat(canonicalParent);
  if (!parentMetadata.isDirectory() || parentMetadata.isSymbolicLink()) {
    throw new ApiError("Snapshot output parent must be a real directory", 400);
  }
  const resolvedPath = join(canonicalParent, basename(filePath));
  try {
    const metadata = await lstat(resolvedPath);
    if (metadata.isSymbolicLink() || !metadata.isFile()) {
      throw new ApiError("Snapshot output path must not be a symlink or directory", 400);
    }
    return {
      path: resolvedPath,
      exists: true,
      existing: {
        dev: metadata.dev,
        ino: metadata.ino,
        size: metadata.size,
        mtimeMs: metadata.mtimeMs,
      },
    };
  } catch (error) {
    if ((error as NodeJS.ErrnoException).code !== "ENOENT") throw error;
    return { path: resolvedPath, exists: false };
  }
}

async function writeAll(handle: Awaited<ReturnType<typeof open>>, bytes: Buffer): Promise<void> {
  let offset = 0;
  while (offset < bytes.length) {
    const result = await handle.write(bytes, offset, bytes.length - offset, offset);
    if (result.bytesWritten <= 0) throw new ApiError("Unable to finish writing snapshot ZIP", 500);
    offset += result.bytesWritten;
  }
}

async function removeTemp(path: string): Promise<void> {
  try {
    await unlink(path);
  } catch (error) {
    if ((error as NodeJS.ErrnoException).code !== "ENOENT") throw error;
  }
}

async function writeSnapshot(
  destination: SnapshotDestination,
  bytes: Buffer,
  overwrite: boolean,
): Promise<void> {
  const noFollow = fsConstants.O_NOFOLLOW ?? 0;
  if (!overwrite || !destination.existing) {
    if (destination.exists) {
      throw new ConfirmationRequiredError(
        `Snapshot destination already exists: ${destination.path}. Set overwriteExisting=true to replace it.`,
      );
    }
  }

  const tempPath = join(dirname(destination.path), `.copilot-evaluation-${randomUUID()}.tmp`);
  let installed = false;
  try {
    const handle = await open(
      tempPath,
      fsConstants.O_WRONLY | fsConstants.O_CREAT | fsConstants.O_EXCL | noFollow,
      0o600,
    );
    try {
      await writeAll(handle, bytes);
      await handle.sync();
    } finally {
      await handle.close();
    }

    if (destination.exists) {
      const current = await lstat(destination.path);
      const before = destination.existing!;
      if (
        current.isSymbolicLink() ||
        !current.isFile() ||
        current.dev !== before.dev ||
        current.ino !== before.ino ||
        current.size !== before.size ||
        current.mtimeMs !== before.mtimeMs
      ) {
        throw new ApiError(
          "Snapshot destination changed while the download was in progress; refusing to overwrite it",
          409,
        );
      }
      await rename(tempPath, destination.path);
    } else {
      // Hard-linking the completed same-directory temporary file makes creation
      // atomic without rename's overwrite-on-race behavior.
      try {
        await link(tempPath, destination.path);
        await unlink(tempPath);
      } catch (error) {
        if ((error as NodeJS.ErrnoException).code === "EEXIST") {
          throw new ApiError(
            "Snapshot destination appeared while downloading; retry with overwriteExisting=true only after verifying it",
            409,
          );
        }
        throw error;
      }
    }
    installed = true;
  } finally {
    if (!installed) await removeTemp(tempPath);
  }
}

export async function handleDownloadCopilotEvaluationSnapshot(
  api: CopilotEvaluationApi,
  input: unknown,
): Promise<CopilotEvaluationRichResult> {
  const parsed = downloadCopilotEvaluationSnapshotInputSchema.parse(input);
  const destination = await resolveSnapshotDestination(parsed.outputFilePath);
  if (!parsed.confirmDownload) {
    throw new ConfirmationRequiredError(
      `Snapshot download not confirmed. Destination: ${destination.path} ` +
        `(${destination.exists ? "existing file" : "new file"}). Set confirmDownload=true` +
        `${destination.exists ? " and overwriteExisting=true" : ""} to continue.`,
    );
  }
  if (destination.exists && !parsed.overwriteExisting) {
    throw new ConfirmationRequiredError(
      `Snapshot destination already exists: ${destination.path}. Set overwriteExisting=true to replace it.`,
    );
  }

  const snapshot = await api.downloadSnapshot(
    parsed.agentId,
    parsed.evaluationRunId,
    parsed.environment,
  );
  if (
    snapshot.bytes.length !== snapshot.sizeBytes ||
    snapshot.bytes.length > COPILOT_EVALUATION_MAX_SNAPSHOT_BYTES
  ) {
    throw new ApiError(
      "Copilot evaluation snapshot metadata does not match its bounded bytes",
      502,
    );
  }
  const digest = createHash("sha256").update(snapshot.bytes).digest("hex");
  if (digest !== snapshot.sha256) {
    throw new ApiError("Copilot evaluation snapshot SHA-256 verification failed", 502);
  }
  await writeSnapshot(destination, snapshot.bytes, parsed.overwriteExisting ?? false);
  const metadata = {
    outputFilePath: destination.path,
    sizeBytes: snapshot.sizeBytes,
    sha256: snapshot.sha256,
    contentType: snapshot.contentType,
    suggestedFileName: snapshot.suggestedFileName,
    overwritten: destination.exists,
  };
  return {
    text: [
      `Downloaded Copilot Studio evaluation snapshot to ${destination.path}.`,
      `Size: ${snapshot.sizeBytes} bytes`,
      `SHA-256: ${snapshot.sha256}`,
    ].join("\n"),
    structured: metadata,
  };
}
